import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import html2canvas from 'html2canvas';
import { Image, Page } from '@react-pdf/renderer';
import { useSelector } from 'react-redux';
import { useGetOrderArchiveByIdMutation, usePostOrderByStatusMutation } from '../../store/services/api';
import OrderArchiveTableTop from './OrderArchiveTableTop';
import OrderArchiveTableData from './OrderArchiveTableData';
import FormTextInputRow from '../../components/Form/FormTextInputRow';
import FormDatePicker from '../../components/Form/FormDatePicker';
import GeneralDetailModal from '../OrderDashboard/GeneralDetailModal';
import { chunkArray, getOrderListHtml } from '../../components/Dashboard/GeneralDashboardFunction';
import PdfDownloadModal from '../Report/PdfDownloadModal';
import IconDownArrow from '../../components/Icon/IconDownArrow';
import { SALE_TERRITORY_TYPE_CONSTANTS_ARCHIVE } from '../SampleReport/FormConstant';
import FormSelect from '../../components/Form/FormSelect';
import { getUser } from '../../store/slices/user';
import { useQuery } from '../../hooks/useQuery';

const OrderArchiveDashboard = () => {
    const query = useQuery();
    const searchedOrderId = query.get('order_id');
    const [isLoading, setIsLoading] = useState(true);
    const [completedOrderPaginationIndex, setCompletedOrderPaginationIndex] = useState(1);
    const [maxCompletedOrder, setMaxCompletedOrder] = useState(0);
    const [completedItems, setCompletedItems] = useState([]);
    const [isGeneralDetailModalOpen, setIsGeneralDetailModalOpen] = useState(false);
    const [customPdfDiv, setCustomPdfDiv] = useState([]);
    const [pdfTitle, setPdfTitle] = useState('pdfSample')
    const [infoData, setInfoData] = useState({});
    const [orderTrigger] = usePostOrderByStatusMutation();
    const [orderByIdTrigger] = useGetOrderArchiveByIdMutation();
    const divRefs = useRef([]);
    const [pdfPageLayout, setPdfPageLayout] = useState([]);
    const [isDownloadModalOpen, setDownloadModalOpen] = useState(false);
    const [isFilterCollapse, setIsFilterCollapse] = useState(false);
    const user = useSelector(getUser);
    const [isTerritoryDisable, setIsTerritoryDisable] = useState(false);
    
    // Helper function to get initial territory email for territory users (first matching instance)
    const getInitialTerritoryEmail = () => {
        if (user.role === 'territory') {
            const matchingTerritory = SALE_TERRITORY_TYPE_CONSTANTS_ARCHIVE.find(element => element.email === user.email);
            return matchingTerritory ? matchingTerritory.name : '';
        }
        return '';
    };

    // Helper function to get filtered territory options based on user role
    const getFilteredTerritoryOptions = () => {
        if (user.role === 'territory') {
            return SALE_TERRITORY_TYPE_CONSTANTS_ARCHIVE
                .filter(element => element.email === user.email)
                .map((element) => ({ key: element.id, value: element.name, extraIdentifier: element.name }));
        }
        return SALE_TERRITORY_TYPE_CONSTANTS_ARCHIVE.map((element) => ({ key: element.id, value: element.name, extraIdentifier: element.name }));
    };

    const [customFilterValues, setCustomFilterValues] = useState({
        ownerEmail: '',
        territoryName: getInitialTerritoryEmail(),
        dispatchedDateFrom : '2025-01-01',
        dispatchedDateTo : '',
        orderNumber: '',
        customerName: '',
        projectName: '',
    });

    const formikCustomFilterValuesSchema = Yup.object().shape({
        ownerEmail: Yup.string(),
        territoryName: Yup.string(),
        dispatchedDateFrom : Yup.date(),
        dispatchedDateTo : Yup.date(),
        orderNumber: Yup.string(),
        customerName: Yup.string(),
        projectName: Yup.string(),
    });

    const getOrderByStatus = async () => {
        try {
            if(searchedOrderId === null) {
                if(user.role === 'territory') {
                    setIsTerritoryDisable(true);
                }
                const result = await orderTrigger({
                    orderStatus: 'Completed',
                    querySetting: { index: completedOrderPaginationIndex, limit: 15, customFilter: customFilterValues }
                });
                setCompletedItems(result.data.orderData);
                setMaxCompletedOrder(result.data.totalItem);
            }
            setIsLoading(false);
        } catch (e) {
            toast.error('Unkown error please try again!')
        }
    };

    useEffect( () => {
        setIsLoading(true);
        getOrderByStatus('Completed');
    }, [completedOrderPaginationIndex]);

    const getDataWithPagination = async (currentStatus ,newPaginationIndex) => {
        setCompletedOrderPaginationIndex(completedOrderPaginationIndex + newPaginationIndex);
    }

    const reset = async () => {
        setCustomFilterValues({
            ownerEmail: '',
            territoryName: getInitialTerritoryEmail(),
            dispatchedDateFrom: '2025-01-01',
            dispatchedDateTo: '',
            orderNumber: '',
            customerName: '',
            projectName: '',
        });
        setCompletedOrderPaginationIndex(1);
        await getOrderByStatus();
    }

    const searchWithCustomFilter = async (formData) => {
        try {
            setIsLoading(true);
            if (formData.orderNumber !== '') {
                const searchIdResponse = await orderByIdTrigger(formData.orderNumber);
                if(searchIdResponse.data.orderData.length > 0) {
                    setCompletedItems(searchIdResponse.data.orderData);
                    setMaxCompletedOrder(searchIdResponse.data.totalItem);
                } else {
                    toast.error(`No order found with order #${formData.orderNumber}`)
                }
                setIsLoading(false);
            } else if (Object.values(formData).every(value => value === '')){
                reset();
            } else {
                await getOrderByStatus();
                setIsLoading(false);
            }
        } catch (error) {
            toast.error(error);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (searchedOrderId) {
            const filterValues = {
                ownerEmail: '',
                territoryName: user.role === 'territory' ? user.email : '',
                dispatchedDateFrom: '',
                dispatchedDateTo: '',
                orderNumber: searchedOrderId,
                customerName: '',
                projectName: '',
            };

            setCustomFilterValues(filterValues);
            searchWithCustomFilter(filterValues);
        }
    }, [searchedOrderId]);


    const infoModalOpenHandler = (index) => {
        setInfoData(completedItems[index]);
        setIsGeneralDetailModalOpen(true);
    }

    const generateOrderPdf = async (orderId) => {
        try {
            setIsLoading(true);
            const [data] = completedItems.filter(element => element.orderId === orderId);
            const currentDate = new Date().toDateString();
            setPdfTitle(`SA${data.orderId}${data.isRevised ? 'R' : ''}_${data.formData.customerName}_${data.formData.projectName}_${currentDate}`);
            const tmpCustomPdfDiv = [];
            const chunkedArray = chunkArray(data.sampleData, 2);

            chunkedArray.forEach((item, index) => {
                tmpCustomPdfDiv.push(getOrderListHtml(item, [], [], index, data, divRefs));
            });
            setCustomPdfDiv(tmpCustomPdfDiv);
        } catch (error) {
            toast.error('Unexpected error happened please try again!');
            setIsLoading(false);
        };
    }

    useEffect(() => {
        if (customPdfDiv.length > 0) {
            const generateImages = async () => {
                try {
                    const refsArray = divRefs.current;

                    // Run html2canvas on all refs in parallel
                    const canvases = await Promise.all(
                        refsArray
                            .filter(ref => ref !== null)
                            .map(ref => html2canvas(ref, { scale: 3 }))
                    );

                    const tmpImageList = canvases.map(canvas => canvas.toDataURL());

                    const tmpPdfPageLayout = tmpImageList.map(item => (
                        <Page key={item} size="A4" wrap={false}>
                            <Image src={item} />
                        </Page>
                    ));

                    setPdfPageLayout(tmpPdfPageLayout);
                    setDownloadModalOpen(true);

                    setTimeout(() => {
                        setIsLoading(false);
                    }, 1000);
                } catch (error) {
                    toast.error("Unexpected error happened please try again!");
                    setIsLoading(false);
                }
            };

            generateImages();
        }
    }, [customPdfDiv]);

    return (
        <section className="h-[calc(100vh_-_2rem)] w-full bg-transparent p-4 rounded">

            <div className="flex flex-row w-full">
                <button
                    type="button"
                    className="flex flex-row p-2 font-normal bg-[#137cc0] rounded w-40"
                    onClick={() => setIsFilterCollapse(!isFilterCollapse)}
                >
                    <div className="flex flex-row justify-between w-full text-white">
                        <h2 className="font-bold">Custom Filter</h2>
                        <IconDownArrow />
                    </div>
                </button>

                <button
                    type="button"
                    className="ml-2 flex flex-row p-2 font-normal bg-[#137cc0] rounded w-40"
                    onClick={() => reset()}
                >
                    <div className="flex flex-row justify-center w-full text-white">
                        <h2 className="font-bold">Reset</h2>
                    </div>
                </button>
            </div>

            <div  className={isFilterCollapse ? 'w-full h-auto' : 'h-0 invisible'}>
                <div className="flex flex-row w-full ">
                    <Formik
                        // eslint-disable-next-line react/jsx-boolean-value
                        enableReinitialize={true}
                        initialValues={customFilterValues}
                        validationSchema={formikCustomFilterValuesSchema}
                        onSubmit={searchWithCustomFilter}
                    >
                        {({ values, handleChange, handleSubmit, resetForm }) => {
                            // Update state when form values change
                            useEffect(() => {
                                setCustomFilterValues(values);
                            }, [values]);

                            return (
                                <Form className="flex flex-row w-full h-full gap-2 py-4 items-center justify-start">
                                    <div className="flex flex-col w-3/12 justify-between">
                                        <FormTextInputRow name="orderNumber" label='Order #:' />
                                        <FormTextInputRow name="ownerEmail" label='Owner Email:' />
                                        <FormSelect
                                            name="territoryName"
                                            label='Sale Territory:'
                                            placeholder="Select Sale Territory"
                                            options={getFilteredTerritoryOptions()}
                                            // isDisable={isTerritoryDisable}
                                        />
                                        <FormTextInputRow name="customerName" label="Customer Name:"/>
                                        <FormTextInputRow name="projectName" label="Project Name:"/>
                                        <FormDatePicker name="dispatchedDateFrom" label='Start Date:' />
                                        <FormDatePicker name="dispatchedDateTo" label='End Date:' />
                                        <div className="flex flex-row w-full h-12 text-[#3C3C3C] text-[11px] justify-end">
                                            <button
                                                type="submit"
                                                id='2'
                                                className="w-40 pl-2 pr-2 mt-1 border-2 border-slate-300 rounded bg-slate-100 text-[11px] text-black font-bold"
                                                disabled={isLoading}
                                            >
                                                Search
                                            </button>
                                        </div>
                                    </div>
                                </Form>
                            )}}
                    </Formik>
                </div>
            </div>

            {customPdfDiv}

            {isLoading &&
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                    <div className="inline-block animate-spin ease-linear rounded-full border-t-4 border-blue-500 border-solid h-12 w-12" />
                    <p className="mt-2 text-blue-500">Please wait...</p>
                </div>
            }

            {!isLoading &&
                <>
                    <OrderArchiveTableTop />
                    <OrderArchiveTableData
                        data={completedItems}
                        orderEditHandleFunction={infoModalOpenHandler}
                        currentStatus="Completed"
                        currentPage={completedOrderPaginationIndex}
                        dataSize={maxCompletedOrder}
                        requestDataFunction={getDataWithPagination}
                        requestOnlyPdfFunction={generateOrderPdf}
                    />
                </>
            }

            {isGeneralDetailModalOpen &&
                <GeneralDetailModal
                    onClose={() => setIsGeneralDetailModalOpen(false)}
                    open={isGeneralDetailModalOpen}
                    data={infoData.formData}
                    isMetric={infoData.isMetric}
                    sampleData={infoData.sampleData}
                    // updateFunction={orderEditUpdateFunction}
                    orderId={infoData.id}
                    trackingNumbers={infoData.trackingNumbers}
                    trackingUrl={infoData.trackingURL}
                    labelUrl={infoData.labelURL}
                />
            }

            {isDownloadModalOpen &&
                <PdfDownloadModal
                    open={isDownloadModalOpen}
                    pdfPageLayout={pdfPageLayout}
                    onClose={() => setDownloadModalOpen(false)}
                    pdfTitle={pdfTitle}
                />
            }
        </section>
    )
};

export default OrderArchiveDashboard;